import Api from '../util/Api';
import type { UserSettingDefinition } from '../types/user-settings';
import {
    applyMergePatch,
    composeMergePatches,
    diffMergePatch,
    getAtPath,
    isPlainObject,
    jsonEquals,
    wrapAtPath,
    type JsonRecord
} from '../util/json-merge-patch';
import { fetchUserSettings, patchUserSettings, sessionUserId } from './user-settings-api';

/**
 * The per-user UX settings store (API-617): cosmetic preferences that follow the user between
 * browsers. One JSON document per user on the server (`GET` / `PATCH users/settings`), one copy of
 * it here, shared by every consumer in the document. React reads it through
 * `hooks/use-user-setting`; code outside React (a drawer's Save handler) calls the functions below.
 *
 * How it behaves:
 *
 * - READS never see the stored value directly. `readUserSetting` passes it through the setting's
 *   own parse-with-defaults function, so an unknown or malformed value reads as the defaults.
 * - FIRST PAINT comes from a localStorage mirror of the last known document, keyed by user id, so
 *   one user never paints another's. The `GET` then replaces it: the server is the truth and the
 *   mirror is only a cache.
 * - WRITES change the local copy at once (every consumer in this tab is told synchronously) and
 *   are sent as one debounced merge patch holding only the keys that changed. An array is always
 *   sent complete, because the server replaces arrays whole. A reset sends `null` for the key.
 * - A FAILED `PATCH` is silent. The local value stays for the session and the unsent change rides
 *   along with the next write; nothing is shown, because these are cosmetic preferences.
 * - NOTHING IS SENT UNTIL THE FIRST `GET` HAS SETTLED, and a `GET` and a `PATCH` never overlap
 *   (both run on one chain). That is what makes "the server answer replaces the local copy" safe:
 *   the answer can never be older than a write it would overwrite.
 * - THE USER IS THE EFFECTIVE USER, the same `userId` the session reports under Login As. Changing
 *   user drops the previous user's unsent writes rather than send them on the new session, and no
 *   request goes out unless the session is, at that moment, for the user the request is about.
 */

/** Quiet time after the last write before the patch is sent. */
export const USER_SETTINGS_PATCH_DEBOUNCE_MS = 500;

export const USER_SETTINGS_MIRROR_PREFIX = 'userSettings';

export type UserSettingsUserId = string | number | null | undefined;

export type UserSettingsStatus = 'idle' | 'loading' | 'loaded' | 'error';

/** localStorage key of one user's mirror. */
export function userSettingsMirrorKey(userId: string | number): string {
    return `${USER_SETTINGS_MIRROR_PREFIX}:${userId}`;
}

type AnyDefinition = UserSettingDefinition<unknown>;

interface UserState {
    /** `''` when nobody is signed in: memory only, no mirror, no requests. */
    userId: string;
    status: UserSettingsStatus;
    /** The last document the server (or, before the first answer, the mirror) gave us. */
    base: JsonRecord;
    /** Old localStorage values shown until the first `GET` says whether the server has its own. */
    seeds: Map<AnyDefinition, unknown>;
    /** The patch being sent right now. */
    inFlight: unknown;
    /** Changes not sent yet. */
    queued: unknown;
    /** `base` with every layer above applied: what consumers read. */
    local: JsonRecord;
    timer: ReturnType<typeof setTimeout> | undefined;
    /** Serialises the `GET` and every `PATCH`. */
    chain: Promise<void>;
    loading: Promise<void> | undefined;
}

const listeners = new Set<() => void>();
const legacyDefinitions = new Set<AnyDefinition>();
let parsedCache = new WeakMap<AnyDefinition, { state: UserState; raw: unknown; value: unknown }>();
let active: UserState = createState('');

function normalizeUserId(userId: UserSettingsUserId): string {
    return userId === null || userId === undefined ? '' : String(userId);
}

function readMirror(userId: string): JsonRecord {
    if (!userId) return {};
    try {
        const parsed: unknown = JSON.parse(
            window.localStorage.getItem(userSettingsMirrorKey(userId)) ?? 'null'
        );
        return isPlainObject(parsed) ? parsed : {};
    } catch {
        // storage unavailable or the mirror is not JSON: paint defaults until the GET answers
        return {};
    }
}

function writeMirror(state: UserState): void {
    if (!state.userId) return;
    try {
        window.localStorage.setItem(
            userSettingsMirrorKey(state.userId),
            JSON.stringify(state.local)
        );
    } catch {
        // storage unavailable or full: the mirror is only a first-paint cache
    }
}

function createState(userId: string): UserState {
    const base = readMirror(userId);
    return {
        userId,
        status: 'idle',
        base,
        seeds: new Map(),
        inFlight: undefined,
        queued: undefined,
        local: base,
        timer: undefined,
        chain: Promise.resolve(),
        loading: undefined
    };
}

function asDocument(value: unknown): JsonRecord {
    return isPlainObject(value) ? value : {};
}

function recompute(state: UserState): void {
    let local: unknown = state.base;
    for (const seed of state.seeds.values()) local = applyMergePatch(local, seed);
    if (state.inFlight !== undefined) local = applyMergePatch(local, state.inFlight);
    if (state.queued !== undefined) local = applyMergePatch(local, state.queued);
    state.local = asDocument(local);
}

function emit(): void {
    for (const listener of Array.from(listeners)) listener();
}

/** The state for `userId`, switching to it first if another user's is loaded. */
function activate(userId: UserSettingsUserId): UserState {
    const id = normalizeUserId(userId);
    if (active.userId !== id) {
        // Unsent writes belong to the previous user's session and must not go out on this one.
        clearTimeout(active.timer);
        active = createState(id);
    }
    return active;
}

/**
 * True while an admin is logged in as this user (Login As). The settings are still the user's own,
 * read and write, but the BROWSER is the admin's, so its old localStorage keys are not this user's
 * to show, to carry over or to remove.
 */
function sessionIsLoginAs(): boolean {
    return Boolean(Api.user?.superUserId);
}

function readLegacy(definition: AnyDefinition, userId: string): unknown {
    // Under Login As the old keys in this browser are the admin's, not this user's (most were
    // never scoped by user): they are neither shown nor carried up into the customer's document.
    if (sessionIsLoginAs()) return undefined;
    try {
        return definition.legacy?.read(userId);
    } catch {
        return undefined;
    }
}

function removeLegacy(definition: AnyDefinition, userId: string): void {
    if (sessionIsLoginAs()) return;
    try {
        definition.legacy?.remove(userId);
    } catch {
        // storage unavailable: nothing to remove
    }
}

/** Once the server holds a value for a setting, its old localStorage key has done its job. */
function removeCarriedLegacyKeys(state: UserState): void {
    for (const definition of legacyDefinitions) {
        if (getAtPath(state.base, definition.path) !== undefined) {
            removeLegacy(definition, state.userId);
        }
    }
}

function schedule(state: UserState): void {
    clearTimeout(state.timer);
    state.timer = setTimeout(() => {
        state.timer = undefined;
        void send(state);
    }, USER_SETTINGS_PATCH_DEBOUNCE_MS);
}

function enqueue(state: UserState, patch: unknown): void {
    state.queued = composeMergePatches(state.queued, patch);
    recompute(state);
    writeMirror(state);
    emit();
    if (state.userId) schedule(state);
}

/**
 * The one-time carry-over, decided when the server's document is known: a setting the server has
 * no value for, and that the old localStorage key does, is patched up. A setting the server
 * already has is left alone.
 */
function carryOver(state: UserState, definition: AnyDefinition): void {
    state.seeds.delete(definition);
    if (!state.userId || getAtPath(state.base, definition.path) !== undefined) return;

    const legacy = readLegacy(definition, state.userId);
    if (legacy === undefined || legacy === null) return;

    // Underneath anything the user changed while the GET was out, so their change wins.
    state.queued = composeMergePatches(wrapAtPath(definition.path, legacy), state.queued);
    schedule(state);
}

/** First sight of a setting for this user: show its old localStorage value until the GET answers. */
function register(state: UserState, definition: AnyDefinition): void {
    if (!definition.legacy || !state.userId) return;
    const isNew = !legacyDefinitions.has(definition);
    legacyDefinitions.add(definition);

    if (state.status === 'loaded') {
        if (!isNew) return;
        // Registered after the GET answered (a lazily loaded page). Deferred: this runs during a
        // render, and the carry-over notifies subscribers.
        queueMicrotask(() => {
            if (state !== active || state.status !== 'loaded') return;
            carryOver(state, definition);
            recompute(state);
            writeMirror(state);
            removeCarriedLegacyKeys(state);
            emit();
        });
        return;
    }

    if (state.seeds.has(definition) || getAtPath(state.local, definition.path) !== undefined) {
        return;
    }
    const legacy = readLegacy(definition, state.userId);
    if (legacy === undefined || legacy === null) return;
    state.seeds.set(definition, wrapAtPath(definition.path, legacy));
    recompute(state);
}

function runExclusive(state: UserState, task: () => Promise<void>): Promise<void> {
    const run = state.chain.then(task);
    state.chain = run.catch(() => undefined);
    return state.chain;
}

function send(state: UserState, opts?: { keepalive?: boolean }): Promise<void> {
    // A write made before anything asked for the document: fetch it first, so the answer to this
    // patch is never the first the store hears from the server.
    if (state === active && state.status === 'idle') void loadUserSettings(state.userId);

    return runExclusive(state, async () => {
        if (state !== active || !isPlainObject(state.queued)) return;
        // The session is no longer this user's: the route would write to someone else's document.
        if (sessionUserId() !== state.userId) return;

        const patch = state.queued;
        state.inFlight = patch;
        state.queued = undefined;

        const saved = await patchUserSettings(patch, opts);
        if (state !== active) return;

        state.inFlight = undefined;
        if (saved) {
            state.base = saved;
            removeCarriedLegacyKeys(state);
        } else {
            // Silent on purpose. The value stays for this session and goes out with the next write.
            state.queued = composeMergePatches(patch, state.queued);
        }
        recompute(state);
        writeMirror(state);
        emit();
    });
}

/**
 * Fetches the user's document once. Safe to call from every consumer: a load in progress is
 * shared, a finished one is not repeated, and a failed one is retried by the next call.
 */
export function loadUserSettings(userId: UserSettingsUserId): Promise<void> {
    const state = activate(userId);
    if (!state.userId || state.status === 'loaded') return Promise.resolve();
    if (state.loading) return state.loading;

    state.status = 'loading';
    const loading = runExclusive(state, async () => {
        // Asked for a user the session is not for: the route would answer someone else's document.
        const document = sessionUserId() === state.userId ? await fetchUserSettings() : undefined;
        if (state !== active) return;

        if (!document) {
            // Keep painting the mirror (and any old localStorage value) and keep local writes.
            state.status = 'error';
            emit();
            return;
        }

        state.status = 'loaded';
        state.base = document;
        for (const definition of legacyDefinitions) carryOver(state, definition);
        state.seeds.clear();
        recompute(state);
        writeMirror(state);
        removeCarriedLegacyKeys(state);
        emit();
    }).finally(() => {
        if (state.loading === loading) state.loading = undefined;
    });
    state.loading = loading;
    return loading;
}

/**
 * The setting's current value for `userId`, through its parse-with-defaults function. Returns the
 * same object until the value really changes, so it is safe as a `useSyncExternalStore` snapshot
 * and as an effect dependency.
 *
 * `definition` must be a module-level constant: it is the cache key.
 */
export function readUserSetting<T>(
    userId: UserSettingsUserId,
    definition: UserSettingDefinition<T>
): T {
    const state = activate(userId);
    register(state, definition);

    const raw = getAtPath(state.local, definition.path);
    const cached = parsedCache.get(definition);
    if (cached && cached.state === state && cached.raw === raw) return cached.value as T;

    let value: T;
    try {
        value = definition.parse(raw);
    } catch {
        value = definition.parse(undefined);
    }
    if (cached && cached.state === state && jsonEquals(cached.value, value)) {
        value = cached.value as T;
    }
    parsedCache.set(definition, { state, raw, value });
    return value;
}

/**
 * Stores `value` for the setting. Applied locally at once and sent, debounced, as a patch holding
 * only what differs from the current stored value.
 */
export function setUserSetting<T>(
    userId: UserSettingsUserId,
    definition: UserSettingDefinition<T>,
    value: T
): void {
    const state = activate(userId);
    register(state, definition);

    const patch = diffMergePatch(getAtPath(state.local, definition.path), value);
    if (patch === undefined) return;
    enqueue(state, wrapAtPath(definition.path, patch));
}

/** Removes the stored value (the patch carries `null` for the key), so it reads as its defaults. */
export function resetUserSetting<T>(
    userId: UserSettingsUserId,
    definition: UserSettingDefinition<T>
): void {
    const state = activate(userId);
    register(state, definition);
    // A reset is a decision: an old localStorage value must not come back in its place.
    state.seeds.delete(definition);
    removeLegacy(definition, state.userId);

    if (getAtPath(state.local, definition.path) === undefined && state.status === 'loaded') {
        recompute(state);
        emit();
        return;
    }
    enqueue(state, wrapAtPath(definition.path, null));
}

export function getUserSettingsStatus(userId: UserSettingsUserId): UserSettingsStatus {
    return activate(userId).status;
}

/** Sends any queued change now instead of waiting out the debounce. */
export function flushUserSettings(opts?: { keepalive?: boolean }): Promise<void> {
    const state = active;
    if (state.timer === undefined) return state.chain;
    clearTimeout(state.timer);
    state.timer = undefined;
    return send(state, opts);
}

/** Notified after every change to the local document or its load status. */
export function subscribeUserSettings(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Test seam: forget everything in memory. localStorage is left to the test. */
export function resetUserSettingsStore(): void {
    clearTimeout(active.timer);
    active = createState('');
    legacyDefinitions.clear();
    parsedCache = new WeakMap();
}

if (typeof window !== 'undefined') {
    // Another tab of the same user saved: adopt its document, keeping this tab's unsent changes.
    window.addEventListener('storage', event => {
        if (!active.userId || event.key !== userSettingsMirrorKey(active.userId)) return;
        active.base = readMirror(active.userId);
        recompute(active);
        emit();
    });

    // A full-document navigation (hub to studio, a legacy page) inside the debounce window would
    // otherwise drop the write.
    window.addEventListener('pagehide', () => {
        void flushUserSettings({ keepalive: true });
    });
}
